import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Card } from '@actual-app/components/card';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { CLOUD_NAMESPACE } from './cloud-i18n';
import type { CloudBudgetBinding } from './useCloudBudgetBinding';

export function CloudBudgetBindingCard({
  binding,
  workspaceId,
  localBudgetId,
  isOffline,
}: Readonly<{
  binding: CloudBudgetBinding;
  workspaceId: string | null;
  localBudgetId: string | null;
  isOffline: boolean;
}>) {
  const { t } = useTranslation(CLOUD_NAMESPACE);
  const [isCreating, setIsCreating] = useState(false);
  const [actionError, setActionError] = useState(false);
  const activeBudgets = binding.budgets.filter(
    budget => budget.status === 'active',
  );
  const selectedBudget = activeBudgets.find(
    budget => budget.id === binding.selectedBudgetId,
  );

  async function createBudget(): Promise<void> {
    setActionError(false);
    setIsCreating(true);
    try {
      await binding.createBudget();
    } catch {
      setActionError(true);
    } finally {
      setIsCreating(false);
    }
  }

  const hasContext = workspaceId !== null && localBudgetId !== null;
  return (
    <Card style={{ margin: 0 }}>
      <section aria-labelledby="cloud-budget-binding-heading">
        <View style={{ padding: 18, gap: 14 }}>
          <View
            style={{
              alignItems: 'center',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 10,
              justifyContent: 'space-between',
            }}
          >
            <h2
              id="cloud-budget-binding-heading"
              style={{ fontSize: 18, margin: 0 }}
            >
              <Trans ns={CLOUD_NAMESPACE}>Cloud budget</Trans>
            </h2>
            <strong
              style={{
                color:
                  binding.selectedBudgetId === null
                    ? theme.pageTextSubdued
                    : theme.noticeText,
              }}
            >
              {binding.selectedBudgetId === null ? (
                <Trans ns={CLOUD_NAMESPACE}>Not linked</Trans>
              ) : (
                <Trans ns={CLOUD_NAMESPACE}>Linked</Trans>
              )}
            </strong>
          </View>

          {localBudgetId === null && (
            <p style={subduedStyle}>
              <Trans ns={CLOUD_NAMESPACE}>
                Open a local budget to link it to cloud metadata.
              </Trans>
            </p>
          )}
          {localBudgetId !== null && workspaceId === null && (
            <p style={subduedStyle}>
              <Trans ns={CLOUD_NAMESPACE}>
                Select a workspace to manage the cloud budget link.
              </Trans>
            </p>
          )}
          {isOffline && binding.selectedBudgetId !== null && (
            <p style={subduedStyle}>
              <Trans ns={CLOUD_NAMESPACE}>
                The saved cloud budget link is available offline and will be
                verified after reconnecting.
              </Trans>
            </p>
          )}

          {hasContext && !isOffline && binding.status === 'loading' && (
            <output style={subduedStyle}>
              <Trans ns={CLOUD_NAMESPACE}>Loading cloud budgets...</Trans>
            </output>
          )}
          {(binding.error !== null || actionError) && (
            <View style={{ gap: 8 }}>
              <p role="alert" style={errorStyle}>
                {t(
                  'Could not manage the cloud budget link. Your local budget remains available.',
                )}
              </p>
              {binding.error !== null && (
                <Button onPress={() => void binding.reload()}>
                  <Trans ns={CLOUD_NAMESPACE}>Retry</Trans>
                </Button>
              )}
            </View>
          )}

          {hasContext && !isOffline && activeBudgets.length > 0 && (
            <label style={{ display: 'grid', gap: 6, maxWidth: 440 }}>
              <span style={{ fontWeight: 600 }}>
                <Trans ns={CLOUD_NAMESPACE}>Active cloud budget</Trans>
              </span>
              <select
                aria-label={t('Active cloud budget')}
                value={binding.selectedBudgetId ?? ''}
                onChange={event =>
                  binding.selectBudget(event.currentTarget.value)
                }
                style={inputStyle}
              >
                <option value="" disabled>
                  <Trans ns={CLOUD_NAMESPACE}>Select a cloud budget</Trans>
                </option>
                {activeBudgets.map(budget => (
                  <option key={budget.id} value={budget.id}>
                    {budget.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {selectedBudget !== undefined && (
            <p style={subduedStyle}>
              {t('This local budget is linked to {{name}}.', {
                name: selectedBudget.name,
              })}
            </p>
          )}
          {hasContext &&
            !isOffline &&
            binding.status === 'ready' &&
            activeBudgets.length === 0 && (
              <View style={{ alignItems: 'flex-start', gap: 8 }}>
                <p style={subduedStyle}>
                  <Trans ns={CLOUD_NAMESPACE}>
                    No cloud budget metadata exists in this workspace.
                  </Trans>
                </p>
                <ButtonWithLoading
                  variant="primary"
                  isLoading={isCreating}
                  isDisabled={isCreating}
                  onPress={() => void createBudget()}
                >
                  {isCreating ? (
                    <Trans ns={CLOUD_NAMESPACE}>
                      Creating cloud budget...
                    </Trans>
                  ) : (
                    <Trans ns={CLOUD_NAMESPACE}>Create cloud budget</Trans>
                  )}
                </ButtonWithLoading>
              </View>
            )}
        </View>
      </section>
    </Card>
  );
}

const subduedStyle = {
  color: theme.pageTextSubdued,
  lineHeight: 1.45,
  margin: 0,
} as const;

const errorStyle = {
  color: theme.errorText,
  lineHeight: 1.45,
  margin: 0,
} as const;

const inputStyle = {
  background: theme.tableBackground,
  border: `1px solid ${theme.formInputBorder}`,
  borderRadius: 5,
  color: theme.formInputText,
  font: 'inherit',
  minHeight: 42,
  padding: '8px 10px',
  width: '100%',
} as const;
